import * as Sentry from '@sentry/react'

const dsn = import.meta.env.VITE_SENTRY_DSN

export function initSentry() {
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.warn('Missing VITE_SENTRY_DSN — error monitoring disabled in this environment.')
    }
    return
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    enabled: import.meta.env.PROD,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,
    // Disable breadcrumbs — they can capture console logs that may contain PII
    maxBreadcrumbs: 0,
    // scrubEvent handles both error and non-error events; cast satisfies the narrower beforeSend signature
    beforeSend: scrubEvent as Sentry.BrowserOptions['beforeSend'],
  })
}

export function scrubEvent(event: Sentry.Event): Sentry.Event | null {
  if (event.request?.data && typeof event.request.data === 'object') {
    const data = event.request.data as Record<string, unknown>
    data.answer_text = undefined
    data.email = undefined
    data.name = undefined
  }
  if (event.user) {
    event.user.email = undefined
    event.user.username = undefined
    event.user.ip_address = undefined
  }
  return event
}

interface ErrorContext {
  sessionId?: string
  userId?: string
  subscriptionTier?: string
  route?: string
}

export function captureException(error: unknown, context?: ErrorContext) {
  Sentry.withScope((scope) => {
    if (context?.userId) {
      scope.setUser({ id: context.userId })
    }
    if (context?.sessionId) {
      scope.setContext('session', { session_id: context.sessionId })
    }
    if (context?.subscriptionTier) {
      scope.setTag('subscription_tier', context.subscriptionTier)
    }
    if (context?.route) {
      scope.setTag('route', context.route)
    }
    Sentry.captureException(error)
  })
}
